import type { TFunction } from 'i18next';
import type { JsonSchema, UISchemaElement } from '@jsonforms/core';
import { humanizeFieldName } from '@/components/audit/auditFormatting';
import { applyFieldRestrictions } from '@/lib/fieldRestrictions';
import { splitIntoEnhancedPageTabs, splitIntoEnhancedPanels, type PageTabDescriptor, type PanelDescriptor } from '@/lib/panelUtils';

type Restrictions = Map<string, 'HIDDEN' | 'READ_ONLY'>;

/** A field as the entity's form shows it. */
export interface FormField {
  /** `versionNo`, or `billingAddress.street` in a value object: the `{field}` of its `DisplayName:`/`Description:` keys. */
  path: string;
  label: string;
  /** The form marks it required. */
  required: boolean;
  /** The field's help text, the form's tooltip; '' when it has none. */
  description: string;
  /** The named group of the form the field sits in, inside its section. */
  group?: string;
}

/** A section of the form — a panel of the detail view — with its fields in the form's order. */
export interface FormSection {
  key: string;
  title: string;
  fields: FormField[];
}

interface UiElement {
  type?: string;
  scope?: string;
  label?: string;
  i18n?: string;
  elements?: UiElement[];
}

interface SectionSource {
  title: string;
  elements: UiElement[];
}

interface PlacedControl {
  scope: string;
  group?: string;
}

const PROPERTIES = '#/properties/';

/**
 * The fields of an entity's form as its generated detail view renders it for a record (the Update form): its
 * sections in order — the page tabs' panels, else the panels, else the form's top-level groups — each field once,
 * where the form first places it. A field the user's role may not see is left out by the rule the detail view
 * applies (`applyFieldRestrictions`). Labels, help texts and section names are the form's own texts. Null when
 * the module holds no Update form.
 */
export function formFieldSections(
  schemaModule: Record<string, unknown>,
  entity: string,
  restrictions: Restrictions,
  t: TFunction,
): FormSection[] | null {
  const schema = schemaModule[`${entity}UpdateSchema`] as JsonSchema | undefined;
  const uiSchema = schemaModule[`${entity}UpdateUiSchema`] as UISchemaElement | undefined;
  if (!schema || !uiSchema) return null;

  const restricted = applyFieldRestrictions(schema, uiSchema, restrictions);
  const sources =
    panelSources(schemaModule, entity, uiSchema, restricted.schema, restrictions, t) ??
    flatSources(restricted.uiSchema, entityLabel(entity, t), t);
  const placed = new Set<string>();
  return sources
    .map((source, index) => ({
      key: String(index),
      title: source.title,
      fields: placedControls(source.elements, undefined, t)
        .filter((control) => firstPlacement(placed, control.scope))
        .map((control) => resolveField(control, restricted.schema, entity, t))
        .filter((field): field is FormField => field !== null),
    }))
    .filter((section) => section.fields.length > 0);
}

/** The entity's name as the UI shows it. */
export const entityLabel = (entity: string, t: TFunction): string =>
  t(`DisplayName:${entity}`, { defaultValue: humanizeFieldName(entity) });

/** The detail view's panels — those of its page tabs, in tab order, when it has tabs — split and restricted as it does. */
function panelSources(
  schemaModule: Record<string, unknown>,
  entity: string,
  uiSchema: UISchemaElement,
  restrictedSchema: JsonSchema,
  restrictions: Restrictions,
  t: TFunction,
): SectionSource[] | null {
  const tabs = schemaModule[`${entity}UpdatePageTabs`] as PageTabDescriptor[] | null | undefined;
  const panels = schemaModule[`${entity}UpdatePanels`] as PanelDescriptor[] | null | undefined;
  const split = tabs?.length
    ? splitIntoEnhancedPageTabs(uiSchema, tabs).flatMap((tab) => tab.panels)
    : splitIntoEnhancedPanels(uiSchema, panels ?? []);
  if (split.length === 0) return null;
  return split.map((panel) => ({
    title: panel.titleKey ? t(panel.titleKey, { defaultValue: panel.title }) : panel.title,
    elements: elementsOf(applyFieldRestrictions(restrictedSchema, panel.uiSchema, restrictions).uiSchema),
  }));
}

/**
 * A form without panels renders its UI schema whole: each named top-level group is a section, and what stands
 * outside one belongs to the section before it — or, at the top, to one named after the entity.
 */
function flatSources(uiSchema: UISchemaElement, entityTitle: string, t: TFunction): SectionSource[] {
  const sources: SectionSource[] = [];
  for (const element of elementsOf(uiSchema)) {
    const title = groupLabel(element, t);
    if (title) sources.push({ title, elements: [...(element.elements ?? [])] });
    else if (sources.length > 0) sources[sources.length - 1].elements.push(element);
    else sources.push({ title: entityTitle, elements: [element] });
  }
  return sources;
}

const elementsOf = (uiSchema: UISchemaElement): UiElement[] => (uiSchema as UiElement).elements ?? [];

/** A named group's label, translated as the form translates it. */
function groupLabel(element: UiElement, t: TFunction): string | undefined {
  if (element.type !== 'Group' || !element.label) return undefined;
  return element.i18n ? t(element.i18n, { defaultValue: element.label }) : element.label;
}

/** Each control in the form's order, with the closest named group around it. */
function placedControls(elements: UiElement[], group: string | undefined, t: TFunction): PlacedControl[] {
  return elements.flatMap((element) =>
    element.type === 'Control' && element.scope
      ? [{ scope: element.scope, group }]
      : placedControls(element.elements ?? [], groupLabel(element, t) ?? group, t),
  );
}

function firstPlacement(placed: Set<string>, scope: string): boolean {
  if (placed.has(scope)) return false;
  placed.add(scope);
  return true;
}

/** The field a control shows, read from the (restricted) schema; null when the schema no longer has it. */
function resolveField({ scope, group }: PlacedControl, schema: JsonSchema, entity: string, t: TFunction): FormField | null {
  if (!scope.startsWith(PROPERTIES)) return null;
  const segments = scope.slice(PROPERTIES.length).split('/properties/');
  const { node, parent } = schemaAt(schema, segments);
  if (!node || !parent) return null;
  const name = segments[segments.length - 1];
  const path = segments.join('.');
  return {
    path,
    label: t(`DisplayName:${entity}.${path}`, { defaultValue: node.title ?? humanizeFieldName(name) }),
    required: parent.required?.includes(name) === true,
    // The form's tooltip falls back to the schema's description the same way (useJsonFormsTranslation).
    description: t(`Description:${entity}.${path}`, { defaultValue: node.description ?? '' }),
    group,
  };
}

/** The schema of a property path, and the object schema that holds it. */
function schemaAt(schema: JsonSchema, segments: string[]): { node?: JsonSchema; parent?: JsonSchema } {
  let parent: JsonSchema | undefined;
  let node: JsonSchema | undefined = schema;
  for (const segment of segments) {
    parent = node;
    node = node?.properties?.[segment];
  }
  return { node, parent };
}
